import { useQueryClient } from "@tanstack/react-query";
import { CircleAlert, CircleCheck, GalleryVerticalEnd, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { ROUTE_PATHS } from "@/shared/constants/route-paths";
import type { UnifiedError } from "@/shared/types/api";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";

import { verifyEmailByToken } from "../api";
import { authQueryKeys } from "../query-options";
import { useToken } from "../store";

type VerificationStatus = "verifying" | "success" | "missing" | "invalid" | "failed";

const STATUS_TEXT: Record<VerificationStatus, { title: string; description: string }> = {
  verifying: { title: "正在验证邮箱", description: "正在确认邮件中的验证链接，请稍候。" },
  success: { title: "邮箱验证成功", description: "你的邮箱已完成验证，可以继续使用账号。" },
  missing: { title: "验证链接不完整", description: "链接缺少验证信息，请从邮件中重新打开完整链接。" },
  invalid: { title: "验证链接已失效", description: "链接无效或已过期，请重新发送验证邮件。" },
  failed: { title: "暂时无法验证邮箱", description: "请求没有完成，请稍后重试。" },
};

export function VerifyEmailContent({ token }: { token: string | null }) {
  const queryClient = useQueryClient();
  const isSignedIn = Boolean(useToken());
  const [status, setStatus] = useState<VerificationStatus>(token ? "verifying" : "missing");
  const [attempt, setAttempt] = useState(0);
  const requestRef = useRef<{ attempt: number; promise: Promise<unknown> } | null>(null);

  useEffect(() => {
    if (!token) return;
    if (requestRef.current?.attempt !== attempt) {
      requestRef.current = {
        attempt,
        promise: verifyEmailByToken({ token }, { silent: true }),
      };
    }

    let active = true;
    void requestRef.current.promise.then(() => {
      if (!active) return;
      setStatus("success");
      void queryClient.invalidateQueries({ queryKey: authQueryKeys.userInfo });
    }, (cause: UnifiedError) => {
      if (!active) return;
      setStatus(cause.reason === "TOKEN_INVALID" ? "invalid" : "failed");
    });
    return () => { active = false; };
  }, [attempt, queryClient, token]);

  const { title, description } = STATUS_TEXT[status];
  const destination = isSignedIn ? ROUTE_PATHS.EDUCATION : ROUTE_PATHS.LOGIN;

  return (
    <div className="flex w-full max-w-md flex-col gap-6">
      <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2 self-center font-medium">
        <span className="flex size-6 items-center justify-center rounded-md bg-primary text-primary-foreground">
          <GalleryVerticalEnd className="size-4" />
        </span>
        Pinbox
      </Link>
      <Card role={status === "verifying" || status === "success" ? "status" : "alert"}>
        <CardHeader className="justify-items-center text-center">
          {status === "verifying" ? <Loader2 className="mb-2 size-9 animate-spin text-muted-foreground" aria-hidden="true" />
            : status === "success" ? <CircleCheck className="mb-2 size-9 text-emerald-600" aria-hidden="true" />
              : <CircleAlert className="mb-2 size-9 text-destructive" aria-hidden="true" />}
          <CardTitle className="text-xl">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        {status !== "verifying" && (
          <CardContent className="flex flex-wrap justify-center gap-2">
            {status === "failed" && <Button type="button" onClick={() => {
              setStatus("verifying");
              setAttempt((current) => current + 1);
            }}>重新验证</Button>}
            <Button asChild variant={status === "success" ? "default" : "outline"}>
              <Link to={destination}>{isSignedIn ? "返回教育申请" : "前往登录"}</Link>
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
